import { authHeaders, getBackendUrl } from "../lib/http";
import { useActiveShopId } from "../store/shop.store";
import { useQuery } from "@tanstack/react-query";
import {
  DollarSign,
  ShoppingCart,
  TrendingUp,
  Package,
  BarChart3,
  Loader2,
  AlertCircle,
  RefreshCw,
  Star,
  Image,
} from "lucide-react";
import { clsx } from "clsx";

interface TopProduct {
  listingId: number;
  title: string;
  count: number;
  revenue: number;
  imageUrl?: string;
}

interface RecentOrder {
  receiptId: number;
  createdAt: string;
  buyerName: string;
  itemCount: number;
  total: number;
  currency: string;
  wasPaid: boolean;
  wasShipped: boolean;
}

interface SalesSummary {
  totalRevenue: number;
  totalOrders: number;
  avgOrderValue: number;
  totalItems: number;
  revenueByDay: { date: string; amount: number }[];
  topProducts: TopProduct[];
  recentOrders: RecentOrder[];
}

async function fetchSummary(): Promise<SalesSummary> {
  const res = await fetch(`${getBackendUrl()}/api/sales-dashboard/summary`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error("Failed to load sales summary");
  const json = await res.json();
  return json.data ?? json;
}

async function fetchStats(): Promise<any> {
  const res = await fetch(`${getBackendUrl()}/api/sales-dashboard/stats`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error("Failed to load sales stats");
  const json = await res.json();
  return json.data ?? json;
}

function formatCurrency(amount: number, currency = "USD") {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
  }).format(amount);
}

function formatDate(dateStr: string | number) {
  const d = typeof dateStr === "string" ? new Date(dateStr) : new Date(dateStr * 1000);
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function SkeletonCard() {
  return (
    <div className="animate-pulse rounded-xl border border-gray-200 bg-white p-5">
      <div className="h-4 w-20 rounded bg-gray-200 mb-3" />
      <div className="h-7 w-28 rounded bg-gray-200 mb-1" />
      <div className="h-3 w-16 rounded bg-gray-100" />
    </div>
  );
}

function SkeletonRow() {
  return (
    <div className="animate-pulse flex items-center gap-4 py-3">
      <div className="h-10 w-10 rounded bg-gray-200" />
      <div className="flex-1 space-y-2">
        <div className="h-4 w-48 rounded bg-gray-200" />
        <div className="h-3 w-24 rounded bg-gray-100" />
      </div>
      <div className="h-4 w-16 rounded bg-gray-200" />
    </div>
  );
}

const STATUS_STYLES: Record<string, string> = {
  paid: "bg-yellow-50 text-yellow-700 border-yellow-200",
  shipped: "bg-blue-50 text-blue-700 border-blue-200",
  completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

export default function SalesDashboardPage() {
  const shopId = useActiveShopId();
  const {
    data: summary,
    isLoading: summaryLoading,
    isError: summaryError,
    error: summaryErr,
    refetch: refetchSummary,
  } = useQuery({
    queryKey: ["sales-dashboard-summary", shopId],
    queryFn: fetchSummary,
  });

  const {
    data: stats,
    isLoading: statsLoading,
    isError: statsError,
    error: statsErr,
    refetch: refetchStats,
  } = useQuery({
    queryKey: ["sales-dashboard-stats", shopId],
    queryFn: fetchStats,
  });

  const isLoading = summaryLoading || statsLoading;
  const hasError = summaryError || statsError;
  const errorMessage =
    (summaryErr as Error)?.message || (statsErr as Error)?.message || "Failed to load dashboard";

  const dailySales = (summary?.revenueByDay || []).map((d) => ({ date: d.date, revenue: d.amount, orders: 0 }));
  const topProducts = summary?.topProducts || [];
  const recentOrders = summary?.recentOrders || [];

  const maxDailyRevenue = dailySales.length
    ? Math.max(...dailySales.map((d) => d.revenue))
    : 0;

  const currency = "USD";

  // Empty state: no data at all and not loading
  const isEmpty =
    !isLoading &&
    !hasError &&
    summary &&
    summary.totalOrders === 0 &&
    dailySales.length === 0;

  return (
    <div className="mx-auto max-w-6xl p-6">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <BarChart3 className="h-6 w-6 text-orange-500" />
            Sales Dashboard
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Track your Etsy shop performance and revenue
          </p>
        </div>
        <button
          onClick={() => {
            refetchSummary();
            refetchStats();
          }}
          className="flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50"
        >
          <RefreshCw className="h-4 w-4" />
          Refresh
        </button>
      </div>

      {/* Error */}
      {hasError && (
        <div className="mb-6 rounded-lg border border-red-200 bg-red-50 p-4 flex items-center gap-2">
          <AlertCircle className="h-5 w-5 text-red-500" />
          <p className="text-sm text-red-700">{errorMessage}</p>
        </div>
      )}

      {/* Empty state */}
      {isEmpty && (
        <div className="text-center py-20">
          <ShoppingCart className="h-14 w-14 text-gray-300 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-gray-700 mb-1">No sales data yet</h2>
          <p className="text-sm text-gray-500 max-w-md mx-auto">
            Connect your Etsy shop and start making sales to see your analytics here.
          </p>
        </div>
      )}

      {/* Top Stats Row */}
      {summaryLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
        </div>
      ) : summary && !isEmpty ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="rounded-xl border border-gray-200 bg-white p-5">
            <div className="flex items-center gap-2 mb-2">
              <div className="rounded-lg bg-emerald-50 p-2">
                <DollarSign className="h-4 w-4 text-emerald-600" />
              </div>
              <span className="text-sm font-medium text-gray-500">Total Revenue</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">
              {formatCurrency(summary.totalRevenue, currency)}
            </p>
          </div>

          <div className="rounded-xl border border-gray-200 bg-white p-5">
            <div className="flex items-center gap-2 mb-2">
              <div className="rounded-lg bg-blue-50 p-2">
                <ShoppingCart className="h-4 w-4 text-blue-600" />
              </div>
              <span className="text-sm font-medium text-gray-500">Total Orders</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">
              {(summary.totalOrders ?? 0).toLocaleString()}
            </p>
          </div>

          <div className="rounded-xl border border-gray-200 bg-white p-5">
            <div className="flex items-center gap-2 mb-2">
              <div className="rounded-lg bg-orange-50 p-2">
                <TrendingUp className="h-4 w-4 text-orange-600" />
              </div>
              <span className="text-sm font-medium text-gray-500">Avg Order Value</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">
              {formatCurrency(summary.avgOrderValue, currency)}
            </p>
          </div>

          <div className="rounded-xl border border-gray-200 bg-white p-5">
            <div className="flex items-center gap-2 mb-2">
              <div className="rounded-lg bg-purple-50 p-2">
                <Package className="h-4 w-4 text-purple-600" />
              </div>
              <span className="text-sm font-medium text-gray-500">Items Sold</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">
              {(summary.totalItems ?? 0).toLocaleString()}
            </p>
          </div>
        </div>
      ) : null}

      {/* Revenue Chart */}
      {statsLoading ? (
        <div className="mb-8 rounded-xl border border-gray-200 bg-white p-6">
          <div className="animate-pulse h-4 w-40 rounded bg-gray-200 mb-6" />
          <div className="animate-pulse flex items-end gap-1 h-48">
            {Array.from({ length: 30 }).map((_, i) => (
              <div
                key={i}
                className="flex-1 rounded-t bg-gray-100"
                style={{ height: `${Math.random() * 80 + 20}%` }}
              />
            ))}
          </div>
        </div>
      ) : dailySales.length > 0 ? (
        <div className="mb-8 rounded-xl border border-gray-200 bg-white p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-1">Revenue (Last 30 Days)</h2>
          <p className="text-sm text-gray-500 mb-6">Daily revenue breakdown</p>

          <div className="flex items-end gap-[3px]" style={{ height: 200 }}>
            {dailySales.map((day, i) => {
              const pct = maxDailyRevenue > 0 ? (day.revenue / maxDailyRevenue) * 100 : 0;
              const barHeight = Math.max(pct, 2);
              return (
                <div
                  key={day.date}
                  className="flex-1 flex flex-col items-center group relative"
                  style={{ height: "100%" }}
                >
                  {/* Tooltip */}
                  <div className="absolute bottom-full mb-2 hidden group-hover:block z-10">
                    <div className="rounded-lg bg-gray-900 px-3 py-1.5 text-xs text-white whitespace-nowrap shadow-lg">
                      <p className="font-medium">{day.date}</p>
                      <p>{formatCurrency(day.revenue, currency)}</p>
                      <p>{day.orders} orders</p>
                    </div>
                  </div>
                  <div className="w-full mt-auto">
                    <div
                      className="w-full rounded-t bg-orange-400 hover:bg-orange-500 transition-colors cursor-pointer"
                      style={{ height: `${barHeight}%`, minHeight: 2 }}
                    />
                  </div>
                  {/* Date label every 5th day */}
                  {i % 5 === 0 && (
                    <span className="text-[10px] text-gray-400 mt-1 whitespace-nowrap">
                      {new Date(day.date).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                      })}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ) : null}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Products */}
        <div className="rounded-xl border border-gray-200 bg-white p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-1 flex items-center gap-2">
            <Star className="h-5 w-5 text-orange-500" />
            Top Products
          </h2>
          <p className="text-sm text-gray-500 mb-4">Best selling items</p>

          {statsLoading ? (
            <div className="space-y-3">
              <SkeletonRow />
              <SkeletonRow />
              <SkeletonRow />
              <SkeletonRow />
              <SkeletonRow />
            </div>
          ) : topProducts.length === 0 ? (
            <div className="text-center py-8">
              <Package className="h-10 w-10 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-500">No product data available</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {topProducts.map((product, idx) => (
                <div key={product.listingId} className="flex items-center gap-3 py-3">
                  <span className="text-sm font-bold text-gray-400 w-5 text-right">
                    {idx + 1}
                  </span>
                  <div className="h-10 w-10 rounded-lg bg-gray-100 flex-shrink-0 overflow-hidden">
                    {product.imageUrl ? (
                      <img
                        src={product.imageUrl}
                        alt=""
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center">
                        <Image className="h-4 w-4 text-gray-300" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {product.title}
                    </p>
                    <p className="text-xs text-gray-500">
                      {product.count} sold
                    </p>
                  </div>
                  <span className="text-sm font-semibold text-gray-900">
                    {formatCurrency(product.revenue, currency)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recent Orders */}
        <div className="rounded-xl border border-gray-200 bg-white p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-1 flex items-center gap-2">
            <ShoppingCart className="h-5 w-5 text-orange-500" />
            Recent Orders
          </h2>
          <p className="text-sm text-gray-500 mb-4">Latest transactions</p>

          {statsLoading ? (
            <div className="space-y-3">
              <SkeletonRow />
              <SkeletonRow />
              <SkeletonRow />
              <SkeletonRow />
              <SkeletonRow />
            </div>
          ) : recentOrders.length === 0 ? (
            <div className="text-center py-8">
              <ShoppingCart className="h-10 w-10 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-500">No recent orders</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="pb-2 text-left font-medium text-gray-500">Date</th>
                    <th className="pb-2 text-left font-medium text-gray-500">Buyer</th>
                    <th className="pb-2 text-center font-medium text-gray-500">Items</th>
                    <th className="pb-2 text-right font-medium text-gray-500">Amount</th>
                    <th className="pb-2 text-right font-medium text-gray-500">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {recentOrders.map((order) => (
                    <tr key={order.receiptId} className="hover:bg-gray-50">
                      <td className="py-2.5 text-gray-600 whitespace-nowrap">
                        {formatDate(order.createdAt)}
                      </td>
                      <td className="py-2.5 text-gray-900 font-medium truncate max-w-[120px]">
                        {order.buyerName}
                      </td>
                      <td className="py-2.5 text-center text-gray-600">
                        {order.itemCount}
                      </td>
                      <td className="py-2.5 text-right font-medium text-gray-900 whitespace-nowrap">
                        {formatCurrency(order.total, order.currency || currency)}
                      </td>
                      <td className="py-2.5 text-right">
                        <span
                          className={clsx(
                            "inline-block rounded-full border px-2 py-0.5 text-xs font-medium capitalize",
                            order.wasShipped
                              ? STATUS_STYLES["shipped"]
                              : order.wasPaid
                              ? STATUS_STYLES["paid"]
                              : STATUS_STYLES["completed"] || "bg-gray-50 text-gray-600 border-gray-200"
                          )}
                        >
                          {order.wasShipped ? "shipped" : order.wasPaid ? "paid" : "pending"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Global loading overlay for initial load */}
      {isLoading && !summary && !stats && (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
        </div>
      )}
    </div>
  );
}
